import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  createInvoice,
  getUserTeams,
  getServices,
  getMaterials,
} from "../services/firebaseService";
import {
  Team,
  InvoiceHeader,
  InvoiceService,
  InvoiceMaterial,
  InvoicePhoto,
  Invoice,
} from "../types";
import "./NewInvoice.css";

const NewInvoice: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [serviceType, setServiceType] = useState<"nde" | "ndm">("nde");

  // Form states
  const [header, setHeader] = useState<InvoiceHeader>({
    title: "",
    project: "",
    pms: "",
    os: "",
    city: "",
    date: "",
    sector: "",
    se: "",
    si: "",
    ali: "",
    kmInit: "",
    kmEnd: "",
    team: "",
    respCoelba: "",
    respOrca: "",
    operHRec: "",
    operHDev: "",
    observation: "",
  });

  const [invoiceServices, setInvoiceServices] = useState<InvoiceService[]>([]);
  const [invoiceMaterials, setInvoiceMaterials] = useState<InvoiceMaterial[]>(
    [],
  );
  const [invoicePhotos, setInvoicePhotos] = useState<InvoicePhoto[]>([]);

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  // Service modal form
  const [selectedService, setSelectedService] = useState<any>(null);
  const [serviceQuantity, setServiceQuantity] = useState<number>(1);
  const [serviceSearchTerm, setServiceSearchTerm] = useState<string>("");
  const [showServiceDropdown, setShowServiceDropdown] =
    useState<boolean>(false);

  // Material modal form
  const [selectedMaterial, setSelectedMaterial] = useState<any>(null);
  const [materialQuantity, setMaterialQuantity] = useState<string>("");
  const [materialAction, setMaterialAction] = useState<string>("");
  const [materialSearchTerm, setMaterialSearchTerm] = useState<string>("");
  const [showMaterialDropdown, setShowMaterialDropdown] =
    useState<boolean>(false);

  // Photo modal form
  const [photoData, setPhotoData] = useState({
    pg: "",
    obs: "",
    material: "",
  });

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      if (userTeams.length > 0) {
        setHeader((prev) => ({ ...prev, team: userTeams[0].id }));
      }
    } catch (error) {
      console.error("Error loading teams:", error);
    }
  };

  const loadServices = async () => {
    try {
      const serviceData = await getServices(serviceType);
      setServices(serviceData);
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (serviceType) {
      loadServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  useEffect(() => {
    loadMaterials();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".searchable-dropdown")) {
        setShowServiceDropdown(false);
        setShowMaterialDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadMaterials = async () => {
    try {
      const materialData = await getMaterials();
      setMaterials(materialData);
    } catch (error) {
      console.error("Error loading materials:", error);
    }
  };

  const setInitialDate = () => {
    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0]; // YYYY-MM-DD format for HTML input
    setHeader((prev) => ({ ...prev, date: formattedDate }));
  };

  const handleHeaderChange = (field: keyof InvoiceHeader, value: string) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY
  const formatDateForSave = (dateString: string): string => {
    if (!dateString) return "";
    if (dateString.includes("/")) return dateString; // Already in DD/MM/YYYY format

    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for HTML input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return "";
    if (dateString.includes("-")) return dateString; // Already in YYYY-MM-DD format

    const [day, month, year] = dateString.split("/");
    return `${year}-${month}-${day}`;
  };

  // Filter functions for searchable dropdowns
  const filteredServices = services.filter((service) => {
    if (!serviceSearchTerm) return true;
    const searchLower = serviceSearchTerm.toLowerCase();
    return (
      (service.code && service.code.toLowerCase().includes(searchLower)) ||
      (service.activity &&
        service.activity.toLowerCase().includes(searchLower)) ||
      (service.service && service.service.toLowerCase().includes(searchLower))
    );
  });

  const filteredMaterials = materials.filter((material) => {
    if (!materialSearchTerm) return true;
    const searchLower = materialSearchTerm.toLowerCase();
    return (
      (material.descriptions &&
        material.descriptions.toLowerCase().includes(searchLower)) ||
      (material.material &&
        material.material.toString().toLowerCase().includes(searchLower))
    );
  });

  // Search handler functions
  const handleServiceSearchChange = (value: string) => {
    setServiceSearchTerm(value);
    setShowServiceDropdown(value.length > 0);
    if (value === "") {
      setSelectedService(null);
    }
  };

  const handleServiceSelection = (service: any) => {
    setSelectedService(service);
    setServiceSearchTerm(
      `${service.code} - ${service.activity || service.service}`,
    );
    setShowServiceDropdown(false);
  };

  const handleMaterialSearchChange = (value: string) => {
    setMaterialSearchTerm(value);
    setShowMaterialDropdown(value.length > 0);
    if (value === "") {
      setSelectedMaterial(null);
    }
  };

  const handleMaterialSelection = (material: any) => {
    setSelectedMaterial(material);
    setMaterialSearchTerm(`${material.descriptions} - ${material.material}`);
    setShowMaterialDropdown(false);
  };

  const handleAddService = () => {
    if (selectedService) {
      const newService: InvoiceService = {
        code: selectedService.code || "",
        description: selectedService.service || "",
        activity: selectedService.activity || "",
        quantity: serviceQuantity,
        price: parseFloat(selectedService.price || "0"),
        factor: parseFloat(selectedService.factor || "1"),
        unid: selectedService.unid || "",
      };

      setInvoiceServices((prev) => [...prev, newService]);
      setShowServiceModal(false);
      setSelectedService(null);
      setServiceQuantity(1);
      setServiceSearchTerm("");
      setShowServiceDropdown(false);
    }
  };

  const handleRemoveService = (index: number) => {
    setInvoiceServices((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddMaterial = () => {
    if (selectedMaterial) {
      const newMaterial: InvoiceMaterial = {
        descriptions: selectedMaterial.descriptions || "",
        material:
          parseInt(selectedMaterial.material) ||
          selectedMaterial.material ||
          "",
        type: selectedMaterial.type || "", // Use type from Firebase defaults/materials/items
        quantity: materialQuantity,
        action: materialAction,
      };

      setInvoiceMaterials((prev) => [...prev, newMaterial]);
      setShowMaterialModal(false);
      setSelectedMaterial(null);
      setMaterialQuantity("");
      setMaterialAction("");
      setMaterialSearchTerm("");
      setShowMaterialDropdown(false);
    }
  };

  const handleRemoveMaterial = (index: number) => {
    setInvoiceMaterials((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddPhoto = () => {
    const newPhoto: InvoicePhoto = {
      pg: photoData.pg,
      obs: photoData.obs,
      material: photoData.material,
    };

    setInvoicePhotos((prev) => [...prev, newPhoto]);
    setShowPhotoModal(false);
    setPhotoData({ pg: "", obs: "", material: "" });
  };

  const handleRemovePhoto = (index: number) => {
    setInvoicePhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return invoiceServices.reduce((total, service) => {
      return total + service.price * service.quantity;
    }, 0);
  };

  const calculateTotalWithFactor = () => {
    return invoiceServices.reduce((total, service) => {
      return total + service.price * service.quantity * service.factor;
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!header.team) {
      alert("Selecione uma equipe");
      return;
    }

    if (!header.title || !header.project || !header.date) {
      alert("Preencha os campos obrigatórios: Título, Projeto e Data");
      return;
    }

    setLoading(true);

    try {
      // Format header with proper date format
      const formattedHeader = {
        ...header,
        date: formatDateForSave(header.date),
      };

      // Convert date to timestamp (midnight UTC of the selected date)
      const dateObj = new Date(header.date);
      const dateTimestamp = Date.UTC(
        dateObj.getFullYear(),
        dateObj.getMonth(),
        dateObj.getDate(),
        0,
        0,
        0,
        0,
      );

      const invoiceData: Partial<Invoice> = {
        id: Date.now().toString(), // Generate unique ID
        header: formattedHeader,
        services: invoiceServices,
        materials: invoiceMaterials,
        photos: invoicePhotos,
        total: calculateTotal(),
        total_wfactor: calculateTotalWithFactor(),
        date: dateTimestamp, // Timestamp version of date
        sent_status: "true_opened",
        sent_date: `true_${dateTimestamp}`, // Format: true_timestamp
        checked: false,
        checked_closed: "false_false", // Format: checked_sent_status
        viewedByFat: false,
        paid: false,
        attached: false,
        error: false,
        sending: true, // Web creation should have sending: true
        created_at: Date.now(),
        created_by: user?.uid,
        team: header.team,
        editedBy: user?.email || user?.displayName || "Unknown",
      };

      await createInvoice(header.team, invoiceData);

      alert("Boletim criado com sucesso!");

      // Reset form
      setHeader({
        title: "",
        project: "",
        pms: "",
        os: "",
        city: "",
        date: "",
        sector: "",
        se: "",
        si: "",
        ali: "",
        kmInit: "",
        kmEnd: "",
        team: "",
        respCoelba: "",
        respOrca: "",
        operHRec: "",
        operHDev: "",
        observation: "",
      });
      setInvoiceServices([]);
      setInvoiceMaterials([]);
      setInvoicePhotos([]);
      setInitialDate();
    } catch (error) {
      console.error("Error creating invoice:", error);
      alert("Erro ao criar boletim. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-invoice-container">
      <div className="new-invoice-header">
        <h1>Novo Boletim</h1>
        <button onClick={() => window.close()} className="close-btn">
          <i className="fas fa-times"></i>
          Fechar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="invoice-form">
        {/* Header Information */}
        <div className="form-section">
          <h3>Informações do Cabeçalho</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Título *</label>
              <input
                type="text"
                value={header.title}
                onChange={(e) => handleHeaderChange("title", e.target.value)}
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Projeto *</label>
              <input
                type="text"
                value={header.project}
                onChange={(e) => handleHeaderChange("project", e.target.value)}
                required
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>PMS</label>
              <input
                type="text"
                value={header.pms}
                onChange={(e) => handleHeaderChange("pms", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>OS</label>
              <input
                type="text"
                value={header.os}
                onChange={(e) => handleHeaderChange("os", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Município</label>
              <input
                type="text"
                value={header.city}
                onChange={(e) => handleHeaderChange("city", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Data *</label>
              <input
                type="date"
                value={formatDateForInput(header.date)}
                onChange={(e) => handleHeaderChange("date", e.target.value)}
                required
                className="form-input"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Setor</label>
              <input
                type="text"
                value={header.sector}
                onChange={(e) => handleHeaderChange("sector", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>SE</label>
              <input
                type="text"
                value={header.se}
                onChange={(e) => handleHeaderChange("se", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>PES</label>
              <input
                type="text"
                value={header.si}
                onChange={(e) => handleHeaderChange("si", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>ALI</label>
              <input
                type="text"
                value={header.ali}
                onChange={(e) => handleHeaderChange("ali", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>KM Inicial</label>
              <input
                type="text"
                value={header.kmInit}
                onChange={(e) => handleHeaderChange("kmInit", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>KM Final</label>
              <input
                type="text"
                value={header.kmEnd}
                onChange={(e) => handleHeaderChange("kmEnd", e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Equipe</label>
              <select
                value={header.team}
                onChange={(e) => handleHeaderChange("team", e.target.value)}
                className="form-select"
              >
                <option value="">Selecione uma equipe</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Resp. Coelba</label>
              <input
                type="text"
                value={header.respCoelba}
                onChange={(e) =>
                  handleHeaderChange("respCoelba", e.target.value)
                }
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Resp. Orca</label>
              <input
                type="text"
                value={header.respOrca}
                onChange={(e) => handleHeaderChange("respOrca", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Oper. H. Rec.</label>
              <input
                type="text"
                value={header.operHRec}
                onChange={(e) => handleHeaderChange("operHRec", e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Oper. H. Dev.</label>
              <input
                type="text"
                value={header.operHDev}
                onChange={(e) => handleHeaderChange("operHDev", e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label>Observação</label>
            <textarea
              value={header.observation}
              onChange={(e) =>
                handleHeaderChange("observation", e.target.value)
              }
              className="form-textarea"
              rows={3}
            />
          </div>
        </div>

        {/* Services Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>Serviços</h3>
            <button
              type="button"
              onClick={() => setShowServiceModal(true)}
              className="add-btn"
            >
              <i className="fas fa-plus"></i>
              Adicionar Serviço
            </button>
          </div>

          {invoiceServices.length > 0 && (
            <div className="items-list">
              {invoiceServices.map((service, index) => (
                <div key={index} className="item-card">
                  <div className="item-info">
                    <strong>{service.code}</strong> - {service.description}
                    <div className="item-details">
                      Qtd: {service.quantity} | Preço: R${" "}
                      {service.price.toFixed(2)} | Fator: {service.factor} |
                      Total: R${" "}
                      {(
                        service.price *
                        service.quantity *
                        service.factor
                      ).toFixed(2)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveService(index)}
                    className="remove-btn"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Materials Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>Materiais</h3>
            <button
              type="button"
              onClick={() => setShowMaterialModal(true)}
              className="add-btn"
            >
              <i className="fas fa-plus"></i>
              Adicionar Material
            </button>
          </div>

          {invoiceMaterials.length > 0 && (
            <div className="items-list">
              {invoiceMaterials.map((material, index) => (
                <div key={index} className="item-card">
                  <div className="item-info">
                    <strong>{material.descriptions}</strong>
                    <div className="item-details">
                      Material: {material.material} | Tipo: {material.type} |
                      Qtd: {material.quantity} | Ação: {material.action}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveMaterial(index)}
                    className="remove-btn"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Photos Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>Estruturas/Fotos</h3>
            <button
              type="button"
              onClick={() => setShowPhotoModal(true)}
              className="add-btn"
            >
              <i className="fas fa-plus"></i>
              Adicionar Estrutura
            </button>
          </div>

          {invoicePhotos.length > 0 && (
            <div className="items-list">
              {invoicePhotos.map((photo, index) => (
                <div key={index} className="item-card">
                  <div className="item-info">
                    <strong>PG: {photo.pg}</strong>
                    <div className="item-details">
                      Material: {photo.material} | Obs: {photo.obs}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(index)}
                    className="remove-btn"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="totals-section">
          <div className="total-item">
            <strong>Total sem Fator: R$ {calculateTotal().toFixed(2)}</strong>
          </div>
          <div className="total-item">
            <strong>
              Total com Fator: R$ {calculateTotalWithFactor().toFixed(2)}
            </strong>
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Criando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Criar Boletim
              </>
            )}
          </button>
        </div>
      </form>

      {/* Service Modal */}
      {showServiceModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowServiceModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adicionar Serviço</h3>
              <button
                onClick={() => setShowServiceModal(false)}
                className="modal-close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Tipo de Serviço</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="serviceType"
                      value="nde"
                      checked={serviceType === "nde"}
                      onChange={(e) =>
                        setServiceType(e.target.value as "nde" | "ndm")
                      }
                    />
                    NDE
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="serviceType"
                      value="ndm"
                      checked={serviceType === "ndm"}
                      onChange={(e) =>
                        setServiceType(e.target.value as "nde" | "ndm")
                      }
                    />
                    NDM
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label>Serviço</label>
                <div className="searchable-dropdown">
                  <input
                    type="text"
                    className="form-input"
                    value={serviceSearchTerm}
                    onChange={(e) => handleServiceSearchChange(e.target.value)}
                    onFocus={() =>
                      setShowServiceDropdown(
                        serviceSearchTerm.length > 0 || services.length > 0,
                      )
                    }
                    placeholder="Pesquise por código, serviço ou atividade..."
                  />
                  {showServiceDropdown && (
                    <div className="dropdown-options">
                      {filteredServices.length > 0 ? (
                        filteredServices.map((service, index) => (
                          <div
                            key={index}
                            className="dropdown-option"
                            onClick={() => handleServiceSelection(service)}
                          >
                            <strong>{service.code}</strong> - {service.service}{" "}
                            - {service.activity}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-option disabled">
                          Nenhum serviço encontrado
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Quantidade</label>
                <input
                  type="number"
                  value={serviceQuantity}
                  onChange={(e) =>
                    setServiceQuantity(parseInt(e.target.value) || 1)
                  }
                  min="1"
                  step="1"
                  className="form-input"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={handleAddService}
                disabled={!selectedService}
                className="modal-submit"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Material Modal */}
      {showMaterialModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowMaterialModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adicionar Material</h3>
              <button
                onClick={() => setShowMaterialModal(false)}
                className="modal-close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Material</label>
                <div className="searchable-dropdown">
                  <input
                    type="text"
                    className="form-input"
                    value={materialSearchTerm}
                    onChange={(e) => handleMaterialSearchChange(e.target.value)}
                    onFocus={() =>
                      setShowMaterialDropdown(
                        materialSearchTerm.length > 0 || materials.length > 0,
                      )
                    }
                    placeholder="Pesquise por descrição ou código do material..."
                  />
                  {showMaterialDropdown && (
                    <div className="dropdown-options">
                      {filteredMaterials.length > 0 ? (
                        filteredMaterials.map((material, index) => (
                          <div
                            key={index}
                            className="dropdown-option"
                            onClick={() => handleMaterialSelection(material)}
                          >
                            <strong>{material.descriptions}</strong> -{" "}
                            {material.material} - {material.type}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-option disabled">
                          Nenhum material encontrado
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Quantidade</label>
                <input
                  type="text"
                  value={materialQuantity}
                  onChange={(e) => setMaterialQuantity(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Ação</label>
                <select
                  value={materialAction}
                  onChange={(e) => setMaterialAction(e.target.value)}
                  className="form-select"
                >
                  <option value="">Selecione uma ação</option>
                  <option value="INSTALADO">INSTALADO</option>
                  <option value="RETIRADO">RETIRADO</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={handleAddMaterial}
                disabled={!selectedMaterial}
                className="modal-submit"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {showPhotoModal && (
        <div className="modal-overlay" onClick={() => setShowPhotoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adicionar Estrutura</h3>
              <button
                onClick={() => setShowPhotoModal(false)}
                className="modal-close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>PG</label>
                <input
                  type="text"
                  value={photoData.pg}
                  onChange={(e) =>
                    setPhotoData((prev) => ({ ...prev, pg: e.target.value }))
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Material</label>
                <input
                  type="text"
                  value={photoData.material}
                  onChange={(e) =>
                    setPhotoData((prev) => ({
                      ...prev,
                      material: e.target.value,
                    }))
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Observação</label>
                <input
                  type="text"
                  value={photoData.obs}
                  onChange={(e) =>
                    setPhotoData((prev) => ({ ...prev, obs: e.target.value }))
                  }
                  className="form-input"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={handleAddPhoto}
                className="modal-submit"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewInvoice;
